import { useState, useEffect, useCallback, useRef } from 'react';
import { IProjectAsset, AssetType, CreateProjectAssetRequest, UpdateProjectAssetRequest } from '@packages/global';
import { projectAssetApi } from '../services/user/projectAsset.api';
import { useToast } from '../stores/toastStore';

interface UseAssetsParams {
  projectId?: string;
  episodeId?: string;
  /** 是否启用自动加载，默认 true。弹窗关闭时传 false 可跳过请求 */
  enabled?: boolean;
}

/**
 * 统一的资源管理 Hook
 * 自动判断三种场景：
 * 1. 有 episodeId → 剧集资源
 * 2. 有 projectId 但无 episodeId → 项目资源
 * 3. 都没有 → 用户个人资源
 */
export function useAssets(params?: UseAssetsParams | string) {
  // 兼容旧代码：直接传 projectId 字符串
  const projectId = typeof params === 'string' ? params : params?.projectId;
  const episodeId = typeof params === 'object' ? params?.episodeId : undefined;

  const [assets, setAssets] = useState<IProjectAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const toast = useToast();

  // 使用 ref 避免 toast 变化导致重新加载
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const initializedRef = useRef(false);

  // 是否启用自动加载（默认 true，弹窗类组件可传 false 在关闭时跳过请求）
  const enabled = typeof params === 'object' ? params?.enabled !== false : true;

  const loadAssets = useCallback(async () => {
    // 只在首次加载时显示 loading，后续刷新静默进行避免页面抖动
    if (!initializedRef.current) {
      setLoading(true);
    }
    try {
      let data: IProjectAsset[];

      if (episodeId) {
        // 获取剧集资源
        data = await projectAssetApi.getEpisodeAssets(episodeId);
      } else if (projectId) {
        // 获取项目资源
        data = await projectAssetApi.getProjectAssets(projectId);
      } else {
        // 获取用户个人资源
        data = await projectAssetApi.getUserAssets();
      }

      setAssets(data);
      setInitialized(true);
      initializedRef.current = true;
    } catch (err: any) {
      toastRef.current.error(err.message || '加载资源失败');
    } finally {
      setLoading(false);
    }
  }, [projectId, episodeId]);

  useEffect(() => {
    if (enabled) {
      loadAssets();
    }
  }, [loadAssets, enabled]);

  const createAsset = async (data: CreateProjectAssetRequest): Promise<boolean> => {
    try {
      await projectAssetApi.createProjectAsset(data);
      toastRef.current.success('资源创建成功');
      await loadAssets();
      return true;
    } catch (err: any) {
      toastRef.current.error(err.message || '创建资源失败');
      return false;
    }
  };

  const updateAsset = async (id: string, data: UpdateProjectAssetRequest): Promise<boolean> => {
    try {
      await projectAssetApi.updateProjectAsset(id, data);
      toastRef.current.success('资源更新成功');
      await loadAssets();
      return true;
    } catch (err: any) {
      toastRef.current.error(err.message || '更新资源失败');
      return false;
    }
  };

  const deleteAsset = async (id: string): Promise<boolean> => {
    try {
      await projectAssetApi.deleteProjectAsset(id);
      toastRef.current.success('资源删除成功');
      await loadAssets();
      return true;
    } catch (err: any) {
      toastRef.current.error(err.message || '删除资源失败');
      return false;
    }
  };

  const getAssetsByType = useCallback((type: AssetType): IProjectAsset[] => {
    return assets.filter(asset => asset.assetType === type);
  }, [assets]);

  /**
   * 局部更新单个资源（避免刷新整个列表导致页面抖动）
   */
  const updateAssetLocal = useCallback((assetId: string, updates: Partial<IProjectAsset>) => {
    setAssets(prev => prev.map(asset =>
      asset.id === assetId ? { ...asset, ...updates } : asset
    ));
  }, []);

  /**
   * 根据 ID 获取单个资源的最新数据并局部更新
   */
  const refreshAsset = useCallback(async (assetId: string) => {
    try {
      // 重新加载整个列表，但不设置 loading 状态避免闪烁
      let data: IProjectAsset[];

      if (episodeId) {
        data = await projectAssetApi.getEpisodeAssets(episodeId);
      } else if (projectId) {
        data = await projectAssetApi.getProjectAssets(projectId);
      } else {
        data = await projectAssetApi.getUserAssets();
      }

      // 找到更新的资源
      const updatedAsset = data.find(a => a.id === assetId);
      if (updatedAsset) {
        // 只更新这一个资源
        setAssets(prev => prev.map(asset =>
          asset.id === assetId ? updatedAsset : asset
        ));
      }
    } catch (err: any) {
      // 静默失败，不显示错误
      console.error('刷新资源失败:', err);
    }
  }, [projectId, episodeId]);

  return {
    assets,
    loading,
    initialized,
    loadAssets,
    createAsset,
    updateAsset,
    deleteAsset,
    getAssetsByType,
    updateAssetLocal,
    refreshAsset
  };
}

// 保留旧的导出以兼容现有代码
export const useProjectAssets = useAssets;
